import { ChartTable } from './ChartTable';
import { SERIES_BG, SERIES_STROKE, type SeriesColour } from './scale';
import { cn } from '@/util/utils';

export interface DonutItem {
    label: string;
    value: number;
}

export interface DonutChartProps {
    items: DonutItem[];
    format: (value: number) => string;
    title: string;
    /** Said where the ring would be when every slice is zero. */
    emptyNote: string;
    className?: string;
}

const SIZE = 120;
const RADIUS = 46;
const STROKE = 18;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The Reports doughnut (charts.js `donut`): one ring of slices in the palette's order with a
 * legend under it. A slice worth nothing is dropped rather than drawn as a hairline, and a ring
 * of nothing at all is a sentence, not an empty circle. Past six slices the colours would repeat,
 * so the rest are folded into the last.
 */
export function DonutChart({ items, format, title, emptyNote, className }: DonutChartProps) {
    const slices = fold(items.filter((item) => item.value > 0));
    const total = slices.reduce((sum, item) => sum + item.value, 0);

    if (total === 0) {
        return <p className="text-muted-foreground py-4 text-sm">{emptyNote}</p>;
    }

    const offsets = slices.map((_, i) =>
        slices.slice(0, i).reduce((sum, item) => sum + (item.value / total) * CIRCUMFERENCE, 0),
    );

    return (
        <figure className={cn('m-0 flex min-w-0 flex-col items-center gap-4', className)}>
            <svg
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                className="block aspect-square w-[min(72%,220px)]"
                role="img"
                aria-label={title}
            >
                <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
                    {slices.map((item, i) => {
                        const length = (item.value / total) * CIRCUMFERENCE;
                        return (
                            <circle
                                key={item.label}
                                data-slice={item.label}
                                cx={SIZE / 2}
                                cy={SIZE / 2}
                                r={RADIUS}
                                fill="none"
                                strokeWidth={STROKE}
                                strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
                                strokeDashoffset={-(offsets[i] ?? 0)}
                                className={SERIES_STROKE[i as SeriesColour]}
                            />
                        );
                    })}
                </g>
            </svg>
            <figcaption className="grid w-full grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                {slices.map((item, i) => (
                    <span key={item.label} className="flex min-w-0 items-center gap-2 text-sm">
                        <i
                            aria-hidden
                            className={cn(
                                'size-2.5 shrink-0 rounded-full',
                                SERIES_BG[i as SeriesColour],
                            )}
                        />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        <span className="text-muted-foreground tabular-nums">
                            {format(item.value)}
                        </span>
                    </span>
                ))}
            </figcaption>
            <ChartTable
                caption={title}
                columns={['', title]}
                rows={slices.map((item) => ({ label: item.label, cells: [format(item.value)] }))}
            />
        </figure>
    );
}

function fold(items: DonutItem[]): DonutItem[] {
    if (items.length <= SERIES_STROKE.length) return items;
    const kept = items.slice(0, SERIES_STROKE.length - 1);
    const rest = items.slice(SERIES_STROKE.length - 1).reduce((sum, item) => sum + item.value, 0);
    return [...kept, { label: 'Other', value: rest }];
}
